import { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Clock, SignIn, SignOut as SignOutIcon, Calendar } from "@phosphor-icons/react";

export default function TimeClock() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState("self");
  const [openRec, setOpenRec] = useState(null);
  const [list, setList] = useState([]);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [empName, setEmpName] = useState("");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async (eid = selected) => {
    const [s, l, e] = await Promise.all([
      api.get("/timeclock/status", { params: { employee_id: eid === "self" ? null : eid } }),
      api.get("/timeclock/list", { params: { employee_id: eid === "self" ? null : eid, month } }),
      api.get("/employees"),
    ]);
    setOpenRec(s.data.open);
    setList(l.data);
    setEmployees(e.data);
  }, [selected, month]);

  useEffect(() => { refresh(); }, [refresh]);

  const checkin = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/timeclock/checkin", { employee_id: selected === "self" ? null : selected });
      toast.success("Entrata registrata");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore timbratura");
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/timeclock/checkout", { employee_id: selected === "self" ? null : selected });
      toast.success("Uscita registrata");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore timbratura");
    } finally {
      setBusy(false);
    }
  };

  const addEmployee = async () => {
    if (!empName.trim()) return;
    await api.post("/employees", { name: empName });
    setEmpName(""); setShowAddEmp(false); toast.success("Aggiunto"); refresh();
  };

  const totalHours = list.reduce((s, x) => s + (x.hours || 0), 0);

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Modulo</div>
        <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">Timbra Ore</h1>
      </div>

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4 mb-4">
        <label className="text-xs uppercase tracking-wider text-[#A3A3A3] font-bold mb-2 block">Persona</label>
        <select data-testid="emp-select" value={selected} onChange={(e) => setSelected(e.target.value)}
          className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 text-base focus:border-[#FFD600] focus:outline-none">
          <option value="self">Io stesso</option>
          {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <button data-testid="add-emp-toggle" onClick={() => setShowAddEmp(!showAddEmp)} className="text-[#FFD600] text-sm font-bold uppercase mt-2">+ Aggiungi dipendente</button>
        {showAddEmp && (
          <div className="flex gap-2 mt-2">
            <input data-testid="add-emp-name" value={empName} onChange={(e) => setEmpName(e.target.value)} placeholder="Nome dipendente"
              className="flex-1 bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none" />
            <button data-testid="add-emp-save" onClick={addEmployee} className="bg-[#FFD600] text-[#0A0A0A] h-12 px-4 rounded-md font-black uppercase">OK</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <button data-testid="checkin-btn" onClick={checkin} disabled={!!openRec || busy}
          className="bg-[#FFD600] text-[#0A0A0A] h-20 text-xl font-black uppercase rounded-md hover:bg-[#E6C200] transition disabled:opacity-40 flex flex-col items-center justify-center gap-1">
          <SignIn size={28} weight="fill" /> {busy ? "..." : "Entrata"}
        </button>
        <button data-testid="checkout-btn" onClick={checkout} disabled={!openRec || busy}
          className="bg-[#FF3B30] text-white h-20 text-xl font-black uppercase rounded-md transition disabled:opacity-40 flex flex-col items-center justify-center gap-1">
          <SignOutIcon size={28} weight="fill" /> {busy ? "..." : "Uscita"}
        </button>
      </div>

      {openRec && (
        <div className="bg-[#FFD600]/10 border border-[#FFD600]/30 rounded-md p-3 mb-4 flex items-center gap-2" data-testid="open-rec">
          <Clock size={20} weight="fill" className="text-[#FFD600]" />
          <span className="text-sm">In corso da <b>{new Date(openRec.check_in).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</b></span>
        </div>
      )}

      <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-4">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-2">
            <Calendar size={18} weight="bold" className="text-[#FFD600]" />
            <input type="month" data-testid="month-filter" value={month} onChange={(e) => setMonth(e.target.value)}
              className="bg-[#0A0A0A] border border-[#333] rounded h-9 px-2 text-sm" />
          </div>
          <div className="font-heading text-2xl font-black text-[#FFD600]">{totalHours.toFixed(1)}h</div>
        </div>
        <div className="space-y-2">
          {list.length === 0 ? <div className="text-[#A3A3A3] text-sm">Nessuna timbratura</div> : list.map((t) => (
            <div key={t.id} className="bg-[#0A0A0A] rounded p-3 flex justify-between text-sm" data-testid={`tc-row-${t.id}`}>
              <div>
                <div>{new Date(t.check_in).toLocaleDateString("it-IT")}</div>
                <div className="text-[#A3A3A3] text-xs">{new Date(t.check_in).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} → {t.check_out ? new Date(t.check_out).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
              </div>
              <div className="font-mono text-[#FFD600] font-bold">{t.hours?.toFixed(2) || "—"}h</div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
